import { useEffect, useState, useRef } from 'react';
import Badge from 'models/Badge';
import BadgePopup from 'view/components/common/BadgePopup';
import { useUserContext } from 'contexts/UserContext';
import useApiCall from 'contexts/ApiCall';
import userAuthService from 'services/userAuthService';
import { useLocation } from 'react-router-dom';

function useBadgePopup() {
  const [visible, setVisible] = useState<boolean>(false);
  const [badges, setBadges] = useState<Badge[]>([]);
  const { user } = useUserContext();
  const [activeBadge, setActiveBadge] = useState<Badge | null>();
  const makeApiCall = useApiCall();
  const location = useLocation();
  const previousPathRef = useRef<string>("");
  const fetchedPathsRef = useRef<Set<string>>(new Set());

  // Only fetch badges when user is logged in and when visiting a new pathname
  useEffect(() => {
    if (!user?.id) return;

    // Extract main path segments (e.g., /teacher/classroom/12345 -> teacher/classroom)
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const mainPath = pathSegments.length > 0 ? `/${pathSegments[0]}/${pathSegments[1] || ''}` : '/';

    // Only fetch if we haven't fetched for this main path before
    if (!fetchedPathsRef.current.has(mainPath)) {
      fetchedPathsRef.current.add(mainPath);

      makeApiCall(userAuthService.getUserBadges)
        .then((respBadges) => setBadges(respBadges))
        .catch((error) => console.error(error));
    }

    previousPathRef.current = location.pathname;
  }, [user?.id, location.pathname, makeApiCall]);

  const showBadgePopup = (badge: Badge) => {
    setActiveBadge(badge);
    setVisible(true);
  };

  const handleBadgeModalClosed = () => {
    if (!activeBadge) return;

    setBadges((prev) => prev.filter((a) => a !== activeBadge));
    setActiveBadge(null);
    setVisible(false); // This was set to true, which is likely a bug

    makeApiCall(userAuthService.acknowledgeUserBadge, activeBadge.user_badge)
      .catch((error) => console.error(error));
  };

  const BadgeModal = () =>
    visible ? (
      <BadgePopup
        badge={activeBadge ?? undefined}
        onClose={handleBadgeModalClosed}
      />
    ) : null;

  // Added a function to reset the fetched paths cache if needed
  const resetFetchedPaths = () => {
    fetchedPathsRef.current.clear();
  };

  return {
    badges,
    showBadgePopup,
    BadgeModal,
    isVisible: visible,
    resetFetchedPaths
  };
}

export default useBadgePopup;
